import { useState, useEffect } from "react";
import { useLazyGetStepStaticCardsQuery } from "../../../../service/customersupport-service";
import { StepStatus } from "../../../../model/workflows/supervisor-steps";
import Tabs, { TabItem } from "../../../../common/components/xepp/tab/Tabs";
import { SupervisorStepsTable } from "./SupervisorStepsTable";
import { DateRangeFilter } from "../../../../common/components/xepp/date-range-filter/DateRangeFilter";
import { MonthFilter }  from "../../../../common/components/xepp/month-filter";
import Button from "../../../../common/components/xepp/button/Button";
import { FilterIcon2 } from "../../../../assets/icons";
import {
	OpenRequestsIcon,
	ClosedRequestsIcon,
	UnassignedRequestsIcon,
	OverdueRequestsIcon
} from "../../../../assets/icons/supervisor-cards";
import { ClipLoader } from "react-spinners";
import styles from "./steps.module.scss";

interface SupervisorStepsPageProps {
	departmentId: string;
}

export const SupervisorStepsPage = ({ departmentId }: SupervisorStepsPageProps) => {
	// Generate last 12 months options
	const getLast12Months = () => {
		const months = [];
		const now = new Date();

		for (let i = 0; i < 12; i++) {
			const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
			months.push({
				value: `${date.getFullYear()}-${date.getMonth()}`,
				label: date.toLocaleDateString("en-US", { month: "long", year: "numeric" }),
				date: date,
			});
		}

		return months;
	};

	const monthOptions = getLast12Months();

	const [activeTab, setActiveTab] = useState<StepStatus>(StepStatus.Unassigned);
	// Separate filters: cards month filter and table date range filter
	const [selectedCardMonth, setSelectedCardMonth] = useState<string>(monthOptions[0].value);
	const [isMonthFilterOpen, setIsMonthFilterOpen] = useState(false);
	// Default table filter: 1st of current month to today
	const now = new Date();
	const [fromDate, setFromDate] = useState<Date | null>(new Date(now.getFullYear(), now.getMonth(), 1));
	const [toDate, setToDate] = useState<Date | null>(now);
	const [isDateFilterOpen, setIsDateFilterOpen] = useState(false);
	const [getStaticCards, { data: cardsData, isFetching: isCardsFetching }] = useLazyGetStepStaticCardsQuery();

	const getPercentageColor = (percentage: number): string => {
		if (percentage < 0) return "#DC3545"; // Red
		if (percentage === 0) return "#FFC107"; // Yellow
		return "#28A745"; // Green
	};

	const formatPercentage = (percentage: number): string => {
		const absPercentage = Math.abs(percentage);
		const sign = percentage > 0 ? "+" : percentage < 0 ? "-" : "";
		return `${sign}${absPercentage.toFixed(0)}%`;
	};

	const getMonthDateRange = (monthValue: string) => {
		const [year, month] = monthValue.split("-").map(Number);
		const start = new Date(year, month, 1);
		const end = new Date(year, month + 1, 0, 23, 59, 59);
		return { start, end };
	};

	useEffect(() => {
		if (departmentId && selectedCardMonth) {
			const { start, end } = getMonthDateRange(selectedCardMonth);
			const params: any = {
				departmentId,
				from: start.toISOString(),
				to: end.toISOString(),
			};
			getStaticCards(params);
		}
	}, [departmentId, selectedCardMonth, getStaticCards]);

	const handleApplyDateFilter = (from: Date | null, to: Date | null) => {
		setFromDate(from);
		setToDate(to);
	};

	const formatDateRange = () => {
		if (!fromDate || !toDate) return "Add Filter";
		const from = fromDate.toLocaleDateString("en-US", { month: "short", day: "numeric" });
		const to = toDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
		return `${from} - ${to}`;
	};
	const hasActiveDateFilter = Boolean(fromDate || toDate);

	const getSelectedMonthLabel = () => {
		const selected = monthOptions.find((option) => option.value === selectedCardMonth);
		return selected?.label || monthOptions[0].label;
	};

	const handleMonthApply = (monthValue: string) => {
		setSelectedCardMonth(monthValue);
	};

	const cards = cardsData ?? {
		openRequests: 0,
		closedRequests: 0,
		unassignedRequests: 0,
		overdueRequests: 0,
		openRequestsPercentageChange: 0,
		closedRequestsPercentageChange: 0,
		unassignedRequestsPercentageChange: 0,
		overdueRequestsPercentageChange: 0,
	};

	const commonTableProps = {
		departmentId,
		fromDate,
		toDate,
		onOpenDateFilter: () => setIsDateFilterOpen(true),
		onClearDateFilter: () => handleApplyDateFilter(null, null),
		dateFilterLabel: formatDateRange(),
		hasActiveDateFilter,
	};

	const tabs: TabItem[] = [
		{
			key: StepStatus.Unassigned.toString(),
			label: "Unassigned",
			content: <SupervisorStepsTable {...commonTableProps} stepStatus={StepStatus.Unassigned} />,
		},
		{
			key: StepStatus.Assigned.toString(),
			label: "Assigned",
			content: <SupervisorStepsTable {...commonTableProps} stepStatus={StepStatus.Assigned} />,
		},
		{
			key: StepStatus.Closed.toString(),
			label: "Closed",
			content: <SupervisorStepsTable {...commonTableProps} stepStatus={StepStatus.Closed} />,
		},
		{
			key: StepStatus.Overdue.toString(),
			label: "Overdue",
			content: <SupervisorStepsTable {...commonTableProps} stepStatus={StepStatus.Overdue} />,
		},
	];

	return (
		<div>
			<div className={styles.cardsHeader}>
				<h3 className={styles.cardsTitle}>Summary Statistics</h3>
				<div className={styles.cardsHeaderActions}>
					{isCardsFetching && <ClipLoader size={20} color="#DE841B" />}
					<Button
						text={getSelectedMonthLabel()}
						variant="outline"
						onClick={() => setIsMonthFilterOpen(true)}
						leftIcon={<FilterIcon2 />}
					/>
				</div>
			</div>
			<div className={styles.supervisorCardsContainer}>
				<div className={styles.supervisorCard}>
					<div className={styles.cardLabel}>Open Requests</div>
					<div className={styles.cardValue}>{cards.openRequests}</div>
					<div className={styles.cardPercentage}>
						<div className={styles.cardIcon}>
							<OpenRequestsIcon />
						</div>
						<span
							className={styles.percentageValue}
							style={{ color: getPercentageColor(cards.openRequestsPercentageChange) }}
						>
							{formatPercentage(cards.openRequestsPercentageChange)}
						</span>
						<span className={styles.percentageText}>from last period</span>
					</div>
				</div>
				<div className={styles.supervisorCard}>
					<div className={styles.cardLabel}>Closed Requests</div>
					<div className={styles.cardValue}>{cards.closedRequests}</div>
					<div className={styles.cardPercentage}>
						<div className={styles.cardIcon}>
							<ClosedRequestsIcon />
						</div>
						<span
							className={styles.percentageValue}
							style={{ color: getPercentageColor(cards.closedRequestsPercentageChange) }}
						>
							{formatPercentage(cards.closedRequestsPercentageChange)}
						</span>
						<span className={styles.percentageText}>from last period</span>
					</div>
				</div>
				<div className={styles.supervisorCard}>
					<div className={styles.cardLabel}>Unassigned Requests</div>
					<div className={styles.cardValue}>{cards.unassignedRequests}</div>
					<div className={styles.cardPercentage}>
						<div className={styles.cardIcon}>
							<UnassignedRequestsIcon />
						</div>
						<span
							className={styles.percentageValue}
							style={{ color: getPercentageColor(cards.unassignedRequestsPercentageChange) }}
						>
							{formatPercentage(cards.unassignedRequestsPercentageChange)}
						</span>
						<span className={styles.percentageText}>from last period</span>
					</div>
				</div>
				<div className={styles.supervisorCard}>
					<div className={styles.cardLabel}>Overdue Requests</div>
					<div className={styles.cardValue}>{cards.overdueRequests}</div>
					<div className={styles.cardPercentage}>
						<div className={styles.cardIcon}>
							<OverdueRequestsIcon />
						</div>
						<span
							className={styles.percentageValue}
							style={{ color: getPercentageColor(cards.overdueRequestsPercentageChange) }}
						>
							{formatPercentage(cards.overdueRequestsPercentageChange)}
						</span>
						<span className={styles.percentageText}>from last period</span>
					</div>
				</div>
			</div>
			<Tabs
				tabs={tabs}
				activeTab={activeTab.toString()}
				onTabChange={key => setActiveTab(parseInt(key) as StepStatus)}
				hideBorder={false}
			/>
			<DateRangeFilter
				isOpen={isDateFilterOpen}
				onClose={() => setIsDateFilterOpen(false)}
				onApply={handleApplyDateFilter}
				initialFrom={fromDate}
				initialTo={toDate}
			/>
			<MonthFilter
				isOpen={isMonthFilterOpen}
				onClose={() => setIsMonthFilterOpen(false)}
				onApply={handleMonthApply}
				initialMonth={selectedCardMonth}
				monthOptions={monthOptions}
			/>
		</div>
	);
};
